import { Checkbox, getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { FluentUIPieChart, IPieChartDataPoint } from 'components/Charts/PieChart';
import KeyValueTagPicker from 'components/Pickers/KeyValueTagPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import RiskOwnerDTO from 'models/dto/riskOwnerDTO';
import Tag from 'models/tag';
import React, { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataRiskOwner } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { generatePalette } from 'utils/color';
import { newGuidNil } from 'utils/guid';
import { sortOnNumber } from 'utils/sorting';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetRiskOwnerConfig {
  tagIds: number[];

  showTags: boolean;

  tags: Tag[];

  constructor() {
    this.tagIds = [];
    this.showTags = false;
    this.tags = [];
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.tagIds = newRawConfig.tagIds ?? [];
        this.showTags = newRawConfig.showTags ?? false;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetRiskOwnerConfig {
    const newConfig = new WidgetRiskOwnerConfig();
    newConfig.tagIds = [...this.tagIds];
    newConfig.tags = [...this.tags];
    newConfig.showTags = this.showTags;

    return newConfig;
  }
}

interface IWidgetRiskOwnerProps extends IWidgetRendererProps {}

const WidgetRiskOwner: FunctionComponent<IWidgetRiskOwnerProps> = (props: IWidgetRiskOwnerProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = React.useContext(AppContext);
  const [data, setData] = React.useState<RiskOwnerDTO[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [riskCount, setRiskCount] = React.useState<number>(0);
  const [config, setConfig] = React.useState<WidgetRiskOwnerConfig | undefined>(undefined);
  const [colorGradient, setColorGradient] = React.useState<string[]>([]);
  const theme = getTheme();

  React.useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();
        setConfig(config);

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);

        const data = await apiGetWidgetDataRiskOwner(config.tags, accessToken, props.abortSignal);

        for (let idx = 0; idx < data.length; idx++) {
          const item = data[idx];
          if (item.ownerId) {
            item.owner = appContext.globalDataCache.users.get(item.ownerId);
          } else if (item.ownerRoleId) {
            item.role = appContext.globalDataCache.roles.get(item.ownerRoleId);
          }
          item.name = item.owner?.name ?? item.role?.name ?? t('translation:General.OwnerPicker.NoOwner');
        }

        setData(data.sort((a, b) => -1 * sortOnNumber(a.riskCount, b.riskCount)));
        setRiskCount(data.reduce((sum, current) => sum + (current.riskCount ?? 0), 0));
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  useEffect(() => {
    if (data.length > 0) {
      setColorGradient(generatePalette(data.length));
    }
  }, [data]);

  const loadConfig = (): WidgetRiskOwnerConfig => {
    const newConfig = new WidgetRiskOwnerConfig();
    newConfig.load(props.widget.widgetConfig);
    newConfig.tags = appContext.globalDataCache.tags.getItemsForId(newConfig.tagIds).filter((t) => t.tagId !== 0);

    return newConfig;
  };

  const getRiskOwnerColor = (idx: number): string => {
    return colorGradient[idx];
  };

  const getData = (): IPieChartDataPoint[] => {
    if (data.length === 0) return [];

    const config = loadConfig();
    const tagStr = `&tags=${config.tags.map((t) => t.tagId.toString()).join(',')}`;

    const graphData: IPieChartDataPoint[] = data.map((s, idx) => {
      const id = s.ownerId ?? s.ownerRoleId ?? newGuidNil();

      return {
        name: s.name ?? '',
        value: s.riskCount ?? 0,
        color: !s.ownerId && !s.ownerRoleId ? theme.palette.neutralSecondaryAlt : getRiskOwnerColor(idx),
        link: `/risks/overview?owner=${id}${config.tags.length > 0 ? tagStr : ''}`,
      };
    });

    return graphData;
  };

  if (!data || isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (riskCount === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:RiskOwner.NoRisks')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapSmall}>
      {config && config.tags.length > 0 && config.showTags && (
        <IPStack.Item styles={{ root: { maxHeight: 44, overflowY: 'hidden' } }}>
          <KeyValueTags tags={config.tags} compact tagMaxWidth={200} />
        </IPStack.Item>
      )}
      <IPStack.Item grow>
        <FluentUIPieChart
          data={getData()}
          height={config && config.tags.length > 0 && config.showTags ? 190 : 250}
          width={280}
          label={riskCount.toString()}
        ></FluentUIPieChart>
      </IPStack.Item>
    </IPStack>
  );
};

export default WidgetRiskOwner;

//
// Config
//

interface IWidgetConfigWidgetRiskOwnerProps extends IWidgetConfigRendererProps {}

export const WidgetConfigRiskOwner: FunctionComponent<IWidgetConfigWidgetRiskOwnerProps> = (
  props: IWidgetConfigWidgetRiskOwnerProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [showTags, setShowTags] = useState<boolean>(false);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetRiskOwnerConfig => {
    let newRawConfig = new WidgetRiskOwnerConfig();
    if (props.widget.widgetConfig) {
      newRawConfig = JSON.parse(props.widget.widgetConfig);
      newRawConfig.tags = appContext.globalDataCache.tags
        .getItemsForId(newRawConfig.tagIds)
        .filter((t) => t.tagId !== 0);
    }

    return newRawConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();

      if (config.tags) {
        setSelectedTags(config.tags);
      }

      setShowTags(config.showTags);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (tags: Tag[], showTags: boolean): WidgetRiskOwnerConfig => {
    const config = new WidgetRiskOwnerConfig();
    config.tagIds = tags.map((t) => t.tagId);
    config.showTags = showTags;

    return config;
  };

  const onAdd = (tag: Tag) => {
    const newTags = [...selectedTags, tag];
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onRemove = (tag: Tag) => {
    const newTags = selectedTags.filter((t) => t.tagId !== tag.tagId);
    setSelectedTags(newTags);
    const config = getConfig(newTags, showTags);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onSetShowTags = (checked: boolean) => {
    setShowTags(checked);
    const config = getConfig(selectedTags, checked);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPLabel>{t('widgets:RiskOwner.Config.Label')}</IPLabel>
        <KeyValueTagPicker
          itemLimit={5}
          selectedTags={selectedTags}
          onAdd={onAdd}
          onRemove={onRemove}
          isLoading={isLoading}
          allowCreate={false}
        ></KeyValueTagPicker>
      </IPStack.Item>
      <IPStack.Item>
        <Checkbox
          checked={showTags}
          label={t('widgets:RiskOwner.Config.LabelShowTags')}
          onChange={(ev, checked) => {
            onSetShowTags(checked ?? false);
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
